import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useQueries } from "@tanstack/react-query"
import { Plus, ArrowRight, Terminal } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Tooltip, TooltipArrow, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { useListBoards } from "@/hooks/useBoard"
import { listBoardAccess } from "@/lib/http/board"
import { getMemberAvatarGradient } from "@/lib/utils/avatar"
import { cn } from "@/lib/utils"
import { ROUTES } from "@/routes/path"
import Loader from "@/components/ui/loader"
import { CreateBoardDialog } from "./components/create-board-dialog"

function getInitials(firstName: string, lastName: string, email: string) {
  if (firstName || lastName) {
    return [firstName, lastName]
      .filter(Boolean)
      .map((s) => s.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2)
  }
  if (email) return email.slice(0, 2).toUpperCase()
  return "?"
}

export default function BoardsList() {
  const navigate = useNavigate()
  const { data: boards, isLoading } = useListBoards()
  const [isCreateOpen, setIsCreateOpen] = useState(false)

  const accessQueries = useQueries({
    queries: (boards ?? []).map((board) => ({
      queryKey: ["boards", board.id, "access"],
      queryFn: () => listBoardAccess(board.id),
      enabled: !!board?.id,
    })),
  })
  const accessByIndex = accessQueries.map((q) => q.data)

  if (isLoading) {
    return (
      <div className="mt-8 flex h-full items-center justify-center">
        <Loader />
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Compact Header */}
      <div className="shrink-0 border-b border-gray-200 bg-white px-4 py-3 dark:border-gray-800 dark:bg-[#0F0F12]">
        <div className="mx-auto flex max-w-[1920px] items-center justify-between">
          <div>
            <h1 className="text-base font-semibold text-gray-900 dark:text-white">Boards</h1>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Create and manage your data dashboards</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(ROUTES.DASHBOARD.EDITOR.QUERY_EDITOR)}
              className="h-7 gap-1.5 px-2.5 text-xs"
            >
              <Terminal className="h-3 w-3" />
              Query Editor
            </Button>
            <Button size="sm" onClick={() => setIsCreateOpen(true)} className="h-7 gap-1.5 px-2.5 text-xs">
              <Plus className="h-3 w-3" />
              New Board
            </Button>
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div className="min-h-0 flex-1 overflow-auto">
        {isLoading ? (
          <div className="mt-8 flex h-full items-center justify-center">
            <Loader />
          </div>
        ) : !boards || boards.length === 0 ? (
          <div className="mt-16 flex flex-1 items-center justify-center">
            <div className="text-center">
              <p className="mb-1 text-sm font-medium text-gray-600 dark:text-gray-400">No boards yet</p>
              <p className="mb-4 text-xs text-gray-500 dark:text-gray-500">Create your first board to get started</p>
              <Button size="sm" onClick={() => setIsCreateOpen(true)} className="h-7 gap-1.5 px-2.5 text-xs">
                <Plus className="h-3 w-3" />
                Create Board
              </Button>
            </div>
          </div>
        ) : (
          <div className="mx-auto max-w-[1920px] p-4">
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
              <table className="w-full border-collapse text-xs">
                <thead className="bg-gray-50 dark:bg-gray-800/50">
                  <tr>
                    <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700 dark:border-gray-700 dark:text-gray-300">
                      Name
                    </th>
                    <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700 dark:border-gray-700 dark:text-gray-300">
                      Description
                    </th>
                    <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700 dark:border-gray-700 dark:text-gray-300">
                      Access
                    </th>
                    <th className="border-b border-gray-200 px-3 py-2 text-right font-semibold text-gray-700 dark:border-gray-700 dark:text-gray-300">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {boards.map((board, index) => {
                    const access = accessByIndex[index]
                    const members = access?.members ?? []
                    const roleGrants = access?.role_grants ?? []
                    const hasAccess = members.length > 0 || roleGrants.length > 0
                    return (
                      <tr
                        key={board.id}
                        onClick={() => navigate(ROUTES.DASHBOARD.BOARDS.DETAIL(board.id))}
                        className="cursor-pointer border-b border-gray-100 transition-colors hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800/50"
                      >
                        <td className="px-3 py-2.5">
                          <div className="font-medium text-gray-900 dark:text-white">{board.name}</div>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="text-gray-600 dark:text-gray-400">
                            {board.description || <span className="text-gray-400 dark:text-gray-500">—</span>}
                          </div>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex flex-wrap items-center gap-1.5">
                            {!hasAccess && <span className="text-gray-400 dark:text-gray-500">—</span>}
                            <TooltipProvider delayDuration={300}>
                              <div className="flex -space-x-2.5">
                                {members.slice(0, 5).map((m) => {
                                  const displayName =
                                    m.first_name || m.last_name ? `${m.first_name} ${m.last_name}`.trim() : m.email
                                  return (
                                    <Tooltip key={m.account_id}>
                                      <TooltipTrigger asChild>
                                        <Avatar className="h-6 w-6 cursor-default border-2 border-white dark:border-gray-900">
                                          <AvatarFallback
                                            className={cn(
                                              "text-[10px] font-medium text-white",
                                              getMemberAvatarGradient(m.account_id)
                                            )}
                                          >
                                            {getInitials(m.first_name, m.last_name, m.email)}
                                          </AvatarFallback>
                                        </Avatar>
                                      </TooltipTrigger>
                                      <TooltipContent side="bottom">
                                        {displayName}
                                        <TooltipArrow />
                                      </TooltipContent>
                                    </Tooltip>
                                  )
                                })}
                                {roleGrants.map((g) => (
                                  <Tooltip key={g.role_id}>
                                    <TooltipTrigger asChild>
                                      <Avatar className="h-6 w-6 cursor-default border-2 border-white dark:border-gray-900">
                                        <AvatarImage
                                          src={`https://avatar.vercel.sh/${encodeURIComponent(g.role_name)}?size=32`}
                                          alt=""
                                        />
                                        <AvatarFallback className="bg-gray-100 text-[10px] font-medium text-gray-600 dark:bg-gray-600 dark:text-gray-300">
                                          {g.role_name?.charAt(0)?.toUpperCase() ?? "?"}
                                        </AvatarFallback>
                                      </Avatar>
                                    </TooltipTrigger>
                                    <TooltipContent side="bottom">
                                      {g.role_name}
                                      <TooltipArrow />
                                    </TooltipContent>
                                  </Tooltip>
                                ))}
                              </div>
                            </TooltipProvider>
                            {members.length > 5 && (
                              <span className="text-[10px] text-gray-500 dark:text-gray-400">
                                +{members.length - 5}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center justify-end">
                            <ArrowRight className="h-3.5 w-3.5 text-gray-400" />
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <CreateBoardDialog open={isCreateOpen} onOpenChange={setIsCreateOpen} />
    </div>
  )
}
